import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await ordersAPI.getAll();
      setOrders(response.data);

      // Calculate total revenue from delivered orders for admin
      if (user && user.role === 'admin') {
        const revenue = response.data
          .filter(order => order.status === 'delivered')
          .reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        setTotalRevenue(revenue);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    try {
      await ordersAPI.cancel(orderId);
      loadOrders();
    } catch (error) {
      alert('Error canceling order: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page">
      <h1 className="page-title">{user && user.role === 'admin' ? 'All Orders' : 'My Orders'}</h1>

      {orders.length === 0 ? (
        <div className="card">
          <p>No orders yet</p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="card order-card">
              <div className="order-header">
                <h3>{order.order_number}</h3>
                <span className={`status-badge status-${order.status}`}>
                  {order.status}
                </span>
              </div>

              <div className="order-details">
                <p><strong>Total:</strong> ${order.total_amount}</p>
                <p>
                  <strong>Delivery Address:</strong><br />
                  {order.delivery_address}, {order.delivery_city}, {order.delivery_state} {order.delivery_zip}
                </p>
                {order.scheduled_delivery_date && (
                  <p>
                    <strong>Scheduled:</strong> {new Date(order.scheduled_delivery_date).toLocaleDateString()}
                  </p>
                )}
                {order.actual_delivery_date && (
                  <p>
                    <strong>Delivered:</strong> {new Date(order.actual_delivery_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {order.items && order.items.length > 0 && (
                <div className="order-items">
                  <strong>Items:</strong>
                  <ul>
                    {order.items.map((item) => (
                      <li key={item.id}>
                        {item.product?.name} x {item.quantity} - ${item.subtotal}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {order.delivery && order.delivery.photos && order.delivery.photos.length > 0 && (
                <div className="delivery-photos">
                  <strong>Delivery Photos:</strong>
                  <div className="photos-grid">
                    {order.delivery.photos.map((photo) => (
                      <div key={photo.id} className="photo-item">
                        <img src={photo.photo_url} alt={photo.caption || 'Delivery photo'} />
                        {photo.caption && <p className="photo-caption">{photo.caption}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(order.status === 'pending' || order.status === 'confirmed') && (
                <button
                  onClick={() => handleCancel(order.id)}
                  className="btn btn-danger"
                >
                  Cancel Order
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {user && user.role === 'admin' && (
        <div className="card revenue-card-small">
          <span className="revenue-label">Total Revenue: </span>
          <span className="revenue-amount-small">${totalRevenue.toFixed(2)}</span>
          <span className="revenue-note-small"> (from delivered orders)</span>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
